/**
 * SERVICE STRIPE
 *
 * Aucune clé secrète Stripe n'est présente dans le navigateur : toutes les
 * opérations passent par le backend, qui détient les clés.
 * - Commande client : clé du restaurant (retrouvée par le backend via la référence)
 * - Abonnement : clé de l'application (utilisateur connecté obligatoire)
 */

import axios from "axios";
import { base_api_url, DEFAULT_STRIPE_PUBLIC_KEY } from "../environment.jsx";

const tokenUtilisateur = () => {
  try {
    return JSON.parse(localStorage.getItem('user'))?.access_token;
  } catch {
    return null;
  }
};

const messageErreur = (error, defaut) =>
  error.response?.data?.error || error.message || defaut;

/**
 * Créer une session Stripe Checkout
 * @param {Object} data - Données de la commande (reference, amount, currency, notes, success_url, cancel_url, customer_email)
 * @param {boolean} data.abonnement - true pour un paiement d'abonnement à l'application
 */
export const createCheckoutSession = async (data) => {
  try {
    const { abonnement, ...donnees } = data;
    const url = `${base_api_url}api/stripe/${abonnement ? 'abonnement' : 'commande'}/checkout-session`;
    const headers = abonnement ? { Authorization: `Bearer ${tokenUtilisateur()}` } : {};

    const response = await axios.post(url, donnees, { headers });
    return response.data; // { sessionId, url, status }
  } catch (error) {
    console.error('❌ Erreur création session Stripe:', error);
    throw new Error(messageErreur(error, 'Erreur Stripe'));
  }
};

/**
 * Vérifier le statut d'un paiement
 * @param {string} sessionId - ID de la session Stripe
 * @param {Object} options
 * @param {string} options.reference - Référence de la commande (paiement de commande)
 * @param {boolean} options.abonnement - true pour un paiement d'abonnement
 */
export const getPaymentStatus = async (sessionId, { reference, abonnement } = {}) => {
  try {
    const url = `${base_api_url}api/stripe/${abonnement ? 'abonnement' : 'commande'}/checkout-session/${sessionId}`;
    const response = await axios.get(url, abonnement
      ? { headers: { Authorization: `Bearer ${tokenUtilisateur()}` } }
      : { params: { reference } });
    return response.data;
  } catch (error) {
    console.error('❌ Erreur vérification paiement:', error);
    throw new Error(messageErreur(error, 'Session introuvable'));
  }
};

/**
 * Obtenir la clé publique Stripe par défaut (une clé publique peut être exposée)
 */
export const getStripePublicKey = () => {
  return DEFAULT_STRIPE_PUBLIC_KEY;
};

// Export pour compatibilité avec l'ancien code
export const STRIPE_PUBLIC_KEY = DEFAULT_STRIPE_PUBLIC_KEY;

/**
 * État de l'abonnement QRNYAMA qui s'applique à l'utilisateur connecté
 * ({ etat: 'actif' | 'grace' | 'expire', abonnement, proprietaire, grace_jours })
 */
export const getEtatAbonnement = async () => {
  const response = await axios.get(`${base_api_url}api/abonnement/etat`, {
    headers: { Authorization: `Bearer ${tokenUtilisateur()}` },
  });
  return response.data;
};

/**
 * Lien vers la page Stripe « Gérer mon abonnement » (carte, factures, résiliation)
 */
export const ouvrirPortailAbonnement = async (returnUrl) => {
  try {
    const response = await axios.post(`${base_api_url}api/stripe/abonnement/portail`, { return_url: returnUrl }, {
      headers: { Authorization: `Bearer ${tokenUtilisateur()}` },
    });
    return response.data.url;
  } catch (error) {
    throw new Error(messageErreur(error, "Impossible d'ouvrir la gestion de l'abonnement"));
  }
};
